/**
 * Client-minted ids. Ground rule #3 of the plan: the client mints the uuid
 * for every row it creates, and every save is an upsert on that id. The
 * server never mints an id for a row the client already holds — that is
 * the root cause of the v2.31.0 duplicate-play bug.
 */
export function newId(): string {
  const c = globalThis.crypto as Crypto | undefined;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  // RFC4122 v4 fallback for very old WebViews.
  const bytes = new Uint8Array(16);
  if (c && typeof c.getRandomValues === 'function') c.getRandomValues(bytes);
  else for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const h = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
export function isUuid(s: unknown): s is string {
  return typeof s === 'string' && UUID_RE.test(s);
}

/**
 * Short ids for items nested INSIDE a jsonb blob (players on a play, lines,
 * drill steps). These never become table rows, so they only need to be
 * unique within their parent. Same shape as the plugin's libNewId().
 */
export function shortId(prefix = ''): string {
  return prefix + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
}

export function nowIso(): string {
  return new Date().toISOString();
}

/**
 * Deterministic uuid-shaped id for rows that have a natural key
 * (attendance: player+date; eval score: player+day+coach; lineup: team).
 * Two devices creating "the same" row offline then converge on one id and
 * last-write-wins instead of colliding on the unique index. FNV-1a 64 ×2
 * with different seeds → 128 bits, formatted as a v4-looking uuid.
 */
export function stableId(key: string): string {
  const h = (seed: bigint): bigint => {
    let x = seed;
    for (let i = 0; i < key.length; i++) {
      x ^= BigInt(key.charCodeAt(i));
      x = (x * 0x100000001b3n) & 0xffffffffffffffffn;
    }
    return x;
  };
  const a = h(0xcbf29ce484222325n).toString(16).padStart(16, '0');
  const b = h(0x84222325cbf29ce4n).toString(16).padStart(16, '0');
  const hex = (a + b).split('');
  hex[12] = '4';
  hex[16] = '89ab'[parseInt(hex[16]!, 16) & 3]!;
  const s = hex.join('');
  return `${s.slice(0, 8)}-${s.slice(8, 12)}-${s.slice(12, 16)}-${s.slice(16, 20)}-${s.slice(20)}`;
}
